<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Column from 'primevue/column'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import DataTable from '@lib/components/shared/DataTable.vue'
import EmptyState from '@lib/components/shared/EmptyState.vue'
import SkeletonRows from '@lib/components/shared/SkeletonRows.vue'
import ExpandRowPanel from '@lib/components/shared/ExpandRowPanel.vue'
import BulkActionBar from '@lib/components/shared/BulkActionBar.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import BBadge from '@lib/components/base/BBadge.vue'
import { useFilters, type FilterParser } from '@lib/composables/useFilters'
import { useFormatters } from '@lib/composables/useFormatters'
import { useToast } from '@lib/composables/useToast'
import { useConfirm } from '@lib/composables/useConfirm'
import { useUiStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { sdk } from '@/graphql/client'
import { useEventsData, type Event, type EventFilters } from '@/composables/useEventsData'
import { computeStatus } from '@/lib/eventStatus'
import EventStatusBadge from '@/components/domain/events/EventStatusBadge.vue'
import EventsFilterBar from '@/components/domain/events/EventsFilterBar.vue'
import EventEditPanel from '@/components/domain/events/EventEditPanel.vue'
import EventMobileCard from '@/components/domain/events/EventMobileCard.vue'
import type { LocationQueryValue } from 'vue-router'

const { t } = useI18n()
const ui = useUiStore()
const auth = useAuthStore()
const toast = useToast()
const { confirm } = useConfirm()
const fmt = useFormatters()

const initialFilters: EventFilters = {
    search: '',
    status: 'all',
    from: null,
    to: null
}

function asString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string {
    if (Array.isArray(raw)) return raw[0] ?? ''
    return raw ?? ''
}
function asNullableString(raw: LocationQueryValue | LocationQueryValue[] | undefined): string | null {
    const v = asString(raw)
    return v === '' ? null : v
}
function asStatus(raw: LocationQueryValue | LocationQueryValue[] | undefined): EventFilters['status'] {
    const v = asString(raw)
    if (v === 'upcoming' || v === 'ongoing' || v === 'past') return v
    return 'all'
}

const parsers: { [K in keyof EventFilters]?: FilterParser<EventFilters[K]> } = {
    status: asStatus,
    from: asNullableString,
    to: asNullableString
}

const { filters } = useFilters<EventFilters>(initialFilters, { parsers })

const data = useEventsData()

// Mirror URL filters → composable filters
watch(
    filters,
    v => {
        data.filters.value = { ...v }
    },
    { immediate: true, deep: true }
)

const expandedId = ref<number | null>(null)
const creating = ref(false)
const selected = ref<Event[]>([])
// Both desktop (md+) and mobile (< md) branches render in the DOM (only one
// is visible per Tailwind hidden classes). Each branch has its own template
// ref to avoid v-for collisions; activeEditRef picks whichever is currently
// mounted with a panel inside.
type EditPanelHandle = { isDirty: () => boolean }
const editPanelRefDesktop = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
const editPanelRefMobile = ref<EditPanelHandle | EditPanelHandle[] | null>(null)
function unwrap(r: EditPanelHandle | EditPanelHandle[] | null): EditPanelHandle | null {
    if (!r) return null
    if (Array.isArray(r)) return r.find(x => x != null) ?? null
    return r
}
function activeEditRef(): EditPanelHandle | null {
    return unwrap(editPanelRefMobile.value) ?? unwrap(editPanelRefDesktop.value)
}

// Sentinel id used for the virtual "new" row.
const NEW_ROW_ID = -1

const expandedRows = computed<Record<string, boolean>>(() => {
    if (creating.value) return { [String(NEW_ROW_ID)]: true }
    if (expandedId.value === null) return {}
    return { [String(expandedId.value)]: true }
})

// Combined list with virtual "new" row first when creating.
const rows = computed<Event[]>(() => {
    if (creating.value) {
        const newRow: Event = { id: NEW_ROW_ID, name: '', startAt: null, endAt: null, bzucoId: 0 }
        return [newRow, ...data.filteredEvents.value]
    }
    return data.filteredEvents.value
})

const isEmpty = computed(() => !data.loading.value && rows.value.length === 0)

onMounted(async () => {
    ui.setBreadcrumbs([{ label: t('events.list.title') }])
    await data.refetch()
})

function safeIsDirty(): boolean {
    try {
        return activeEditRef()?.isDirty() ?? false
    } catch {
        return false
    }
}

async function toggleExpand(id: number) {
    if (expandedId.value === id) {
        if (safeIsDirty()) {
            const ok = await confirm({
                title: t('events.confirm.unsavedChanges'),
                severity: 'danger'
            })
            if (!ok) return
        }
        expandedId.value = null
    } else {
        expandedId.value = id
    }
}

function onRowClick(row: Event) {
    if (row.id === NEW_ROW_ID) return
    toggleExpand(row.id)
}

function clearFilters() {
    filters.value = { ...initialFilters }
}

function startCreate() {
    if (creating.value) return
    expandedId.value = null
    creating.value = true
}

async function onSaved() {
    creating.value = false
    expandedId.value = null
    await data.refetch()
}

async function onDeleted() {
    expandedId.value = null
    await data.refetch()
}

async function onCancel() {
    if (creating.value && safeIsDirty()) {
        const ok = await confirm({
            title: t('events.confirm.unsavedChanges'),
            severity: 'danger'
        })
        if (!ok) return
    }
    creating.value = false
    expandedId.value = null
}

async function onBulkDelete() {
    const deletable = selected.value.filter(e => (e.bzucoId ?? 0) === 0)
    const blocked = selected.value.length - deletable.length
    if (deletable.length === 0) {
        if (blocked > 0) toast.error(t('events.bzucoSynced.tooltip'))
        return
    }
    const ok = await confirm({
        title: t('events.confirm.bulkDeleteTitle'),
        message: t('events.confirm.bulkDeleteBody', { n: deletable.length }),
        severity: 'danger'
    })
    if (!ok) return
    const results = await Promise.allSettled(deletable.map(e => sdk.AdminDeleteEvent({ id: e.id })))
    const okCount = results.filter(r => r.status === 'fulfilled' && r.value.admin?.event?.delete?.ok).length
    const failCount = results.length - okCount
    toast.success(t('events.bulk.deleteResult', { ok: okCount, failed: failCount }))
    selected.value = []
    await data.refetch()
}

// Reset local UI when active instance changes
watch(
    () => auth.invalidationKey,
    () => {
        expandedId.value = null
        creating.value = false
        selected.value = []
        data.refetch()
    }
)
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('events.list.title')">
            <template #actions>
                <BBtn :disabled="creating" @click="startCreate">{{ t('events.actions.newEvent') }}</BBtn>
            </template>
        </PageHeader>

        <EventsFilterBar v-model="filters" @clear="clearFilters" />

        <BulkActionBar :selected-count="selected.length" @clear="selected = []">
            <BBtn
                severity="danger"
                size="small"
                type="button"
                :label="t('events.bulk.deleteSelected')"
                @click="onBulkDelete"
            />
        </BulkActionBar>

        <div class="bg-white border border-gray-200 rounded-12 overflow-hidden">
            <SkeletonRows v-if="data.loading.value && rows.length === 0" :rows="8" :columns="5" />
            <EmptyState v-else-if="isEmpty" :title="t('events.list.empty')" />
            <template v-else>
                <!--
                    Both branches render in the DOM (visibility gated by
                    hidden/md:block/md:hidden). Each owns its own EventEditPanel
                    ref to avoid template-ref collisions across two trees.
                -->
                <!-- DESKTOP (md+): table -->
                <div class="hidden md:block">
                    <DataTable
                        :value="rows"
                        row-key="id"
                        :selection="selected"
                        :expanded-rows="expandedRows"
                        @update:selection="v => (selected = v)"
                        @row-click="onRowClick"
                    >
                        <Column selection-mode="multiple" :style="{ width: '38px' }" />
                        <Column :header="t('events.list.cols.name')">
                            <template #body="{ data: row }">
                                <template v-if="row.id === NEW_ROW_ID">
                                    <em class="text-teal">{{ t('events.actions.newEvent') }}</em>
                                </template>
                                <template v-else>
                                    <span class="font-medium text-ink">{{ row.name }}</span>
                                    <BBadge
                                        v-if="(row.bzucoId ?? 0) > 0"
                                        variant="arch"
                                        :label="t('events.bzucoSynced.badge')"
                                        class="ml-2"
                                    />
                                </template>
                            </template>
                        </Column>
                        <Column :header="t('events.list.cols.startAt')" :style="{ width: '180px' }">
                            <template #body="{ data: row }">
                                <span v-if="row.id !== NEW_ROW_ID && row.startAt" class="text-xs tabular-nums">
                                    {{ fmt.dateTime(row.startAt) }}
                                </span>
                                <span v-else class="text-gray-300">—</span>
                            </template>
                        </Column>
                        <Column :header="t('events.list.cols.endAt')" :style="{ width: '180px' }">
                            <template #body="{ data: row }">
                                <span v-if="row.id !== NEW_ROW_ID && row.endAt" class="text-xs tabular-nums">
                                    {{ fmt.dateTime(row.endAt) }}
                                </span>
                                <span v-else class="text-gray-300">—</span>
                            </template>
                        </Column>
                        <Column :header="t('events.list.cols.status')" :style="{ width: '120px' }">
                            <template #body="{ data: row }">
                                <EventStatusBadge
                                    v-if="row.id !== NEW_ROW_ID"
                                    :status="computeStatus(row.startAt, row.endAt)"
                                />
                            </template>
                        </Column>
                        <template #expansion="{ data: row }">
                            <ExpandRowPanel
                                :title="
                                    row.id === NEW_ROW_ID
                                        ? t('events.actions.newEvent')
                                        : t('events.list.cols.name') + ': ' + row.name
                                "
                                @close="row.id === NEW_ROW_ID ? onCancel() : toggleExpand(row.id)"
                            >
                                <EventEditPanel
                                    ref="editPanelRefDesktop"
                                    :event="row.id === NEW_ROW_ID ? null : row"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </DataTable>
                </div>
                <!-- MOBILE (< md): vertical card list -->
                <ul class="md:hidden divide-y divide-gray-100">
                    <li v-for="row in rows" :key="row.id" class="p-2">
                        <template v-if="row.id === NEW_ROW_ID">
                            <!-- New row: show the edit panel as a standalone card. -->
                            <ExpandRowPanel
                                :title="t('events.actions.newEvent')"
                                @close="onCancel"
                            >
                                <EventEditPanel
                                    ref="editPanelRefMobile"
                                    :event="null"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                        <template v-else>
                            <EventMobileCard
                                :event="row"
                                :selected="selected.some(s => s.id === row.id)"
                                :expanded="expandedId === row.id"
                                @toggle-select="
                                    selected = selected.some(s => s.id === row.id)
                                        ? selected.filter(s => s.id !== row.id)
                                        : [...selected, row]
                                "
                                @toggle-expand="toggleExpand(row.id)"
                            />
                            <ExpandRowPanel
                                v-if="expandedId === row.id"
                                :title="t('events.list.cols.name') + ': ' + row.name"
                                class="mt-2"
                                @close="toggleExpand(row.id)"
                            >
                                <EventEditPanel
                                    ref="editPanelRefMobile"
                                    :event="row"
                                    @saved="onSaved"
                                    @deleted="onDeleted"
                                    @cancel="onCancel"
                                />
                            </ExpandRowPanel>
                        </template>
                    </li>
                </ul>
            </template>
        </div>
    </DefaultLayout>
</template>
